import { vec2 } from "gl-matrix";

import TWEEN from "@tweenjs/tween.js";

import {
  setHalfPixelTextureCoords,
  setPositionsAttrib,
  setTextureCoordsAttrib,
  createMapTbn,
  setTangentsAttribDirect,
  setBitangentsAttribDirect,
  setNormalsAttribDirect,
  setOpacitiesAttrib
} from "./helpers/webgl-helpers";

import { state } from "./index";

import { FUDGED_QUAD_TEXTURE_COORDS, QUAD_POSITIONS, QUAD_WIDTH } from "./models";

import {
  Directions,
  GameState,
  Drawable,
  Positionable,
  TileMap,
  TileSet,
  TileLayer,
  Updateable
} from "./interfaces";

import SpriteRenderer from "./sprite-renderer";

import { coordinatesCanBeReachedFromPosition, outOfBounds } from "./helpers/map-helpers";

export default abstract class Sprite implements Drawable, Positionable, Updateable {
  static readonly DEFAULT_ANIMATION_SPEED = 350;
  static readonly MOVE_AMOUNT = 0.075;
  static readonly MOVE_FUDGE = QUAD_WIDTH / 4;

  abstract name: string;
  abstract textureUrl: string; // We use the textureUrl as the key for the sprite-renderer
  abstract spriteAnimationTween: TWEEN.Tween;
  abstract tileIndex: number;
  abstract tileSize: vec2;
  abstract tileSetDimensions: vec2;

  public vertexPositions: number[];
  public textureCoords: number[];
  public vertexTangents: Float32Array;
  public vertexBitangents: Float32Array;
  public vertexNormals: Float32Array;
  public modelMatrix: number[];
  public opacity: number;
  public texture: WebGLTexture | null;
  public normalTexture: WebGLTexture | null;
  public interactable: boolean = true;
  public spriteRenderer?: SpriteRenderer = null;
  public spriteRendererIndex?: number = null;
  public spriteRenderRenderOrderHint: string = "A";

  public buffers: {
    positions: WebGLBuffer | null;
    normals: WebGLBuffer | null;
    tangents: WebGLBuffer | null;
    bitangents: WebGLBuffer | null;
    textureCoords: WebGLBuffer | null;
    opacities: WebGLBuffer | null;
  };

  protected gl: WebGLRenderingContext;
  protected gameState: GameState;
  protected _pos: vec2;
  protected animationSpeed: number = Sprite.DEFAULT_ANIMATION_SPEED;
  protected animations: { [key: string]: number[] };
  protected currentDirection: Directions;

  private currentIdleAnimation: Directions;
  private programName: string;
  private scale: vec2;

  constructor(gl: WebGLRenderingContext, programName: string) {
    this.gl = gl;
    this.programName = programName;
    this.buffers = {
      positions: gl.createBuffer(),
      textureCoords: gl.createBuffer(),
      normals: gl.createBuffer(),
      tangents: gl.createBuffer(),
      bitangents: gl.createBuffer(),
      opacities: gl.createBuffer()
    };
    this.textureCoords = [...FUDGED_QUAD_TEXTURE_COORDS];
    this.vertexPositions = [...QUAD_POSITIONS];
    this.vertexTangents = new Float32Array(36);
    this.vertexBitangents = new Float32Array(36);
    this.vertexNormals = new Float32Array(36);
    this.animations = {
      up: [0],
      down: [0],
      left: [0],
      right: [0],
      upIdle: [0],
      downIdle: [0],
      rightIdle: [0],
      leftIdle: [0]
    };

    this.scale = [1, 1];
    this.opacity = 1.0;
    this._pos = vec2.create();
    this.updatePos(2, -3);
    this.updateTbn();
  }

  get pos() {
    return this._pos;
  }

  get size(): vec2 {
    return [this.scale[0], this.scale[1]];
  }

  abstract async load(): Promise<boolean>;

  public move(
    direction: Directions,
    tileLayer: TileLayer,
    tileSet: TileSet,
    tileMap: TileMap,
    amount = Sprite.MOVE_AMOUNT
  ) {
    this.setDirection(direction);

    let x = this.pos[0];
    let y = this.pos[1];
    const fromX = x;
    const fromY = y;

    switch (direction) {
      case Directions.Up:
        y = this.pos[1] + amount;
        break;
      case Directions.Down:
        y = this.pos[1] - amount;
        break;
      case Directions.Left:
        x = this.pos[0] - amount;
        break;
      case Directions.Right:
        x = this.pos[0] + amount;
        break;
    }

    if (x === fromX && y === fromY) return;
    if (outOfBounds(x, y, tileMap)) return;

    const positionIsUnreachable = !coordinatesCanBeReachedFromPosition(
      x,
      // y - this.size[1] / 2, // so character doesn't end up with butt half way in the floor
      y,
      fromX,
      fromY,
      tileLayer,
      tileSet
    );

    if (positionIsUnreachable) return;

    this.updatePos(x, y);
  }

  public draw(timestamp) {
    const {
      gl,
      buffers,
      modelMatrix,
      normalTexture,
      texture,
      textureCoords,
      vertexPositions,
      vertexBitangents,
      vertexNormals,
      vertexTangents,
      programName
    } = this;
    const { programWrappers } = state;
    const programWrapper = programWrappers[programName];
    const { cache } = programWrapper;

    if (!buffers.positions) throw Error(`No positions buffer...`);
    if (!buffers.textureCoords) throw Error(`No texture coordinates buffer...`);

    setPositionsAttrib(gl, programName, state, buffers.positions, vertexPositions);
    setTextureCoordsAttrib(gl, programName, state, buffers.textureCoords, textureCoords);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, normalTexture);
    gl.uniform1i(cache.uniforms.uSpriteSampler, 0);
    gl.uniform1i(cache.uniforms.uNormalSampler, 1);

    gl.uniformMatrix4fv(cache.uniforms.uModelMatrix, false, modelMatrix);
    setOpacitiesAttrib(
      gl,
      programName,
      state,
      buffers.opacities,
      QUAD_POSITIONS.map(() => this.opacity)
    );
    setNormalsAttribDirect(gl, programName, state, buffers.normals, vertexNormals);
    setTangentsAttribDirect(gl, programName, state, buffers.tangents, vertexTangents);
    setBitangentsAttribDirect(gl, programName, state, buffers.bitangents, vertexBitangents);
    gl.drawArrays(gl.TRIANGLES, 0, vertexPositions.length / 3);
  }

  public update(timestamp: number) {
    const { scale } = this;
    for (let i = 0; i < this.vertexPositions.length; i = i + 3) {
      this.vertexPositions[i + 0] = scale[0] * QUAD_POSITIONS[i + 0] + this.pos[0];
      this.vertexPositions[i + 1] = scale[1] * QUAD_POSITIONS[i + 1] + this.pos[1] + QUAD_WIDTH / 2; // .5 moves to feet
      this.vertexPositions[i + 2] = QUAD_POSITIONS[i + 2];
    }

    this.setVertexPositionsOnSpriteRenderer();
  }

  public switchToIdleAnimation() {
    this.setDirection(this.currentIdleAnimation);
  }

  public changeAnimationSpeed(speed: number) {
    this.animationSpeed = speed;
    this.setDirection(this.currentDirection, true);
  }

  public setDirection(direction: Directions, force: boolean = false) {
    if (direction === this.currentDirection && force === false) return;
    this.currentDirection = direction;
    this.updateAnimation(direction);

    let nextIdleAnimation = Directions.DownIdle;
    switch (direction) {
      case Directions.Up:
        nextIdleAnimation = Directions.UpIdle;
        break;
      case Directions.Down:
        nextIdleAnimation = Directions.DownIdle;
        break;
      case Directions.Left:
        nextIdleAnimation = Directions.LeftIdle;
        break;
      case Directions.Right:
        nextIdleAnimation = Directions.RightIdle;
        break;
    }

    this.currentIdleAnimation = nextIdleAnimation;
  }

  public updatePos(x: number, y: number) {
    this._pos = vec2.set(this._pos, x, y);
  }

  public setScale(width: number, height: number) {
    this.scale = [this.scale[0] * width, this.scale[1] * height];
    // Origin is in the middle so we expand from the center
    for (let i = 0; i < this.vertexPositions.length; i = i + 3) {
      this.vertexPositions[i + 0] = this.vertexPositions[i + 0] * width;
      this.vertexPositions[i + 1] = this.vertexPositions[i + 1] * height;
      this.vertexPositions[i + 2] = this.vertexPositions[i + 2];
    }
    this.updateTbn();
  }

  public setVertexPositionsOnSpriteRenderer() {
    if (!this.spriteRenderer) return;
    const index = this.spriteRendererIndex;
    this.spriteRenderer.vertexPositions.splice(
      index * SpriteRenderer.VERTEX_POSITIONS_LENGTH,
      SpriteRenderer.VERTEX_POSITIONS_LENGTH,
      ...this.vertexPositions
    );
  }

  public setTbnOnSpriteRenderer() {
    if (!this.spriteRenderer) return;
    const index = this.spriteRendererIndex;
    this.spriteRenderer.vertexTangents.set(
      this.vertexTangents,
      index * SpriteRenderer.TANGENT_LENGTH
    );

    this.spriteRenderer.vertexBitangents.set(
      this.vertexBitangents,
      index * SpriteRenderer.BITANGENT_LENGTH
    );

    this.spriteRenderer.vertexNormals.set(this.vertexNormals, index * SpriteRenderer.NORMAL_LENGTH);
  }

  public setTextureCoordinatesOnSpriteRenderer() {
    if (!this.spriteRenderer) return;
    const index = this.spriteRendererIndex;
    this.spriteRenderer.textureCoords.splice(
      index * SpriteRenderer.TEXTURE_COORDS_LENGTH,
      SpriteRenderer.TEXTURE_COORDS_LENGTH,
      ...this.textureCoords
    );
  }

  public setOpacitiesOnSpriteRenderer() {
    if (!this.spriteRenderer) return;
    const index = this.spriteRendererIndex;
    this.spriteRenderer.opacities.splice(
      index * SpriteRenderer.OPACITIES_LENGTH,
      SpriteRenderer.OPACITIES_LENGTH,
      ...QUAD_POSITIONS.map(() => this.opacity)
    );
  }

  protected updateTbn() {
    let offset = 0;
    for (let i = 0; i < this.vertexPositions.length; i = i + 9) {
      const vertexPosition = this.vertexPositions.slice(i, i + 9);
      const tbn = createMapTbn(vertexPosition);

      // Every vertex in a triangle can have the same tangent/bitangent
      // TODO: why 6 and not 3??
      for (let j = 0; j < 6; j++) {
        this.vertexTangents.set(tbn.tangent, offset);
        this.vertexBitangents.set(tbn.bitangent, offset);
        this.vertexNormals.set(tbn.normal, offset);
        offset += 3;
      }
    }

    this.setTbnOnSpriteRenderer();
  }

  protected updateAnimation(direction: Directions) {
    let animation = this.animations[direction] || [0];
    if (this.spriteAnimationTween) this.spriteAnimationTween.stop();
    const firstAnimationIndex = animation[0];
    const lastAnimationIndex = animation[animation.length - 1];
    this.tileIndex = firstAnimationIndex;
    this.setTextureCoordinates();
    this.spriteAnimationTween = new TWEEN.Tween({ i: firstAnimationIndex })
      .to({ i: lastAnimationIndex }, this.animationSpeed)
      .repeat(Infinity)
      .yoyo(true)
      .onUpdate(({ i }) => {
        const oldIndex = this.tileIndex;
        this.tileIndex = Math.round(i);
        if (this.tileIndex !== oldIndex) this.setTextureCoordinates();
      })
      .start();
  }

  public setTextureCoordinates() {
    const { textureCoords, tileIndex, tileSize, tileSetDimensions } = this;
    setHalfPixelTextureCoords(
      textureCoords,
      FUDGED_QUAD_TEXTURE_COORDS,
      0,
      tileIndex,
      tileSize,
      tileSetDimensions
    );

    this.setTextureCoordinatesOnSpriteRenderer();
  }
}

// Formula for animation frame:

// count from 0
// index_of_row * num_of_sprites_in_a_row + index_of_column
// e.g:
/*

0 0 0 0 0 0
0 0 0 0 0 0
0 0 X 0 0 0
0 0 0 0 0 0
0 0 0 0 0 0

the X is:

2 * 6 + 2 = 14

*/
